import { useEffect, useState } from "react";
import { api, API, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Search, FileDown, FileSpreadsheet, Printer, Eye, Check, X, Trash2 } from "lucide-react";

export default function AdminPlayers() {
  const [players, setPlayers] = useState([]);
  const [meta, setMeta] = useState({ tehsils: [], roles: [], jerseys: [] });
  const [f, setF] = useState({ q: "", tehsil: "", role: "", jersey_size: "", payment_status: "", status: "" });
  const [view, setView] = useState(null);
  const [trialInput, setTrialInput] = useState("");
  const load = async () => {
    const params = Object.fromEntries(Object.entries(f).filter(([,v]) => v));
    const r = await api.get("/admin/players", { params });
    setPlayers(r.data);
  };
  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)); load(); }, []);
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [f]);

  const action = async (id, status, remark = "") => {
    try { await api.put(`/admin/players/${id}/status`, { status, remark }); toast.success(`Player ${status}`); load(); if(view) setView(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const deletePlayer = async (id, name, mobile) => {
    const ok = window.confirm(`Permanently delete ${name} (${mobile})?\n\nThis will remove the registration, payment details, screenshot, trial records and auction history. The mobile number will be released and can be used to register again.\n\nThis action CANNOT be undone.`);
    if (!ok) return;
    try {
      const r = await api.delete(`/admin/players/${id}`);
      toast.success(`Deleted. Mobile ${r.data.mobile_released} released.`);
      load();
      if (view && view.id === id) setView(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };
  const setTrial = async (id) => {
    try { await api.put(`/admin/players/${id}/trial-number`, null, { params: { trial_number: trialInput } }); toast.success("Trial number set"); load(); setTrialInput(""); }
    catch { toast.error("Failed"); }
  };
  const token = localStorage.getItem("ndpl_admin_token");
  const exportUrl = (kind) => `${API}/admin/export/${kind}?token=${token}`;

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div><div className="text-xs uppercase tracking-[0.4em] text-[#39FF14]">Manage</div>
          <h1 className="font-display text-4xl mt-2">Players</h1></div>
        <div className="flex gap-2">
          <button onClick={async () => { const r = await api.get("/admin/export/excel", { responseType: "blob" }); const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = "NDPL-Players.xlsx"; a.click(); }} data-testid="export-excel" className="px-4 py-2 bg-[#39FF14] text-black text-xs uppercase tracking-widest font-bold flex items-center gap-2"><FileSpreadsheet size={14}/>Excel</button>
          <button onClick={async () => { const r = await api.get("/admin/export/pdf", { responseType: "blob" }); const url = URL.createObjectURL(r.data); const a = document.createElement("a"); a.href = url; a.download = "NDPL-Players.pdf"; a.click(); }} data-testid="export-pdf" className="px-4 py-2 bg-[#FFD700] text-black text-xs uppercase tracking-widest font-bold flex items-center gap-2"><FileDown size={14}/>PDF</button>
          <button onClick={() => window.print()} className="px-4 py-2 border border-white/15 text-xs uppercase tracking-widest flex items-center gap-2"><Printer size={14}/>Print</button>
        </div>
      </div>

      {/* Filters */}
      <div className="mt-6 p-4 bg-[#0B132B] border border-white/10 rounded grid grid-cols-2 md:grid-cols-6 gap-3">
        <div className="col-span-2 md:col-span-2 relative">
          <Search className="absolute left-3 top-3 text-white/40" size={16}/>
          <input data-testid="search-players" value={f.q} onChange={(e) => setF({...f, q: e.target.value})} placeholder="Search name or mobile" className="pl-9 w-full bg-black/50 border border-white/15 px-4 py-2 text-sm" />
        </div>
        {[["tehsil", meta.tehsils],["role", meta.roles],["jersey_size", meta.jerseys],["payment_status", ["unpaid","submitted","verified","rejected"]],["status", ["pending","approved","rejected"]]].map(([k, opts]) => (
          <select key={k} data-testid={`filter-${k}`} value={f[k]} onChange={(e) => setF({...f, [k]: e.target.value})} className="bg-black/50 border border-white/15 px-3 py-2 text-sm">
            <option value="" className="bg-[#0B132B]">All {k.replace("_"," ")}</option>
            {opts.map((o) => <option key={o} value={o} className="bg-[#0B132B]">{o}</option>)}
          </select>
        ))}
      </div>

      {/* Table */}
      <div className="mt-4 overflow-x-auto border border-white/10 rounded" data-testid="players-table">
        <table className="w-full text-sm">
          <thead className="bg-[#151F3D] text-white/70">
            <tr>{["Reg", "Name", "Mobile", "Role", "Tehsil", "Jersey", "Payment", "Status", "Actions"].map((h)=>(
              <th key={h} className="px-3 py-3 text-left text-xs uppercase tracking-widest">{h}</th>))}</tr>
          </thead>
          <tbody>
            {players.length === 0 && <tr><td colSpan={9} className="p-8 text-center text-white/40">No players found</td></tr>}
            {players.map((p) => (
              <tr key={p.id} className="border-t border-white/5 hover:bg-white/5">
                <td className="px-3 py-3 font-mono text-xs text-[#FFD700]">{p.registration_number || "—"}</td>
                <td className="px-3 py-3">{p.name}<div className="text-xs text-white/40">Age {p.age}</div></td>
                <td className="px-3 py-3">{p.mobile}</td>
                <td className="px-3 py-3 text-xs">{p.role}</td>
                <td className="px-3 py-3 text-xs">{p.tehsil}</td>
                <td className="px-3 py-3 text-xs">{p.jersey_size}</td>
                <td className="px-3 py-3"><span className={`text-xs px-2 py-1 border ${p.payment_status === "verified" ? "border-[#39FF14] text-[#39FF14]" : p.payment_status === "submitted" ? "border-[#FFD700] text-[#FFD700]" : "border-white/20 text-white/50"}`}>{p.payment_status}</span></td>
                <td className="px-3 py-3"><span className={`text-xs px-2 py-1 border ${p.status === "approved" ? "border-[#39FF14] text-[#39FF14]" : p.status === "rejected" ? "border-[#FF2442] text-[#FF2442]" : "border-[#FFD700] text-[#FFD700]"}`}>{p.status}</span></td>
                <td className="px-3 py-3">
                  <div className="flex gap-1">
                    <button data-testid={`view-${p.id}`} onClick={() => setView(p)} className="p-2 hover:bg-white/10"><Eye size={14}/></button>
                    {p.status !== "approved" && <button data-testid={`approve-${p.id}`} onClick={() => action(p.id, "approved")} className="p-2 hover:bg-[#39FF14]/20 text-[#39FF14]"><Check size={14}/></button>}
                    {p.status !== "rejected" && <button data-testid={`reject-${p.id}`} onClick={() => action(p.id, "rejected")} className="p-2 hover:bg-[#FF2442]/20 text-[#FF2442]"><X size={14}/></button>}
                    <button data-testid={`delete-${p.id}`} onClick={() => deletePlayer(p.id, p.name, p.mobile)} title="Delete registration" className="p-2 hover:bg-[#FF2442]/30 text-[#FF2442]"><Trash2 size={14}/></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {view && (
        <div className="fixed inset-0 bg-black/80 z-50 grid place-items-center p-4" onClick={() => setView(null)}>
          <div className="bg-[#0B132B] border border-white/15 max-w-3xl w-full p-6 rounded-lg overflow-y-auto max-h-[90vh]" onClick={(e)=>e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-3xl">{view.name}</h2>
              <button onClick={() => setView(null)} className="text-white/60"><X /></button>
            </div>
            <div className="mt-4 grid md:grid-cols-2 gap-4">
              <div className="space-y-2 text-sm">
                {[["Mobile", view.mobile],["Age", view.age],["Role", view.role],["Tehsil", view.tehsil],["Jersey", view.jersey_size],["Reg No", view.registration_number || "—"],["Trial No", view.trial_number || "—"],["UTR", view.utr || "—"],["Status", view.status],["Payment", view.payment_status]].map(([l,v])=>(
                  <div key={l} className="flex justify-between border-b border-white/5 py-1"><span className="text-white/50 uppercase text-xs tracking-widest">{l}</span><span className="text-right">{v}</span></div>
                ))}
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-white/60 mb-2">Payment Screenshot</div>
                {view.screenshot_file_id ? <img src={`${fileUrl(view.screenshot_file_id)}?token=${encodeURIComponent(localStorage.getItem("ndpl_admin_token") || "")}`} alt="screenshot" className="w-full max-h-96 object-contain bg-black/30 border border-white/10" /> : <div className="text-white/40 text-sm">Not uploaded</div>}
              </div>
            </div>
            <div className="mt-6 flex items-center gap-2 flex-wrap">
              <input value={trialInput} onChange={(e)=>setTrialInput(e.target.value)} placeholder="Trial number" className="bg-black/50 border border-white/15 px-3 py-2 text-sm" />
              <button onClick={() => setTrial(view.id)} className="px-4 py-2 bg-[#FFD700] text-black text-xs uppercase tracking-widest font-bold">Assign Trial No.</button>
              <div className="flex-1"/>
              <button data-testid="modal-approve" onClick={() => action(view.id, "approved")} className="px-5 py-2 bg-[#39FF14] text-black uppercase text-xs tracking-widest font-bold flex items-center gap-2"><Check size={14}/>Approve</button>
              <button data-testid="modal-reject" onClick={() => action(view.id, "rejected")} className="px-5 py-2 bg-[#FF2442] text-white uppercase text-xs tracking-widest font-bold flex items-center gap-2"><X size={14}/>Reject</button>
              <button data-testid="modal-delete" onClick={() => deletePlayer(view.id, view.name, view.mobile)} className="px-5 py-2 border border-[#FF2442] text-[#FF2442] hover:bg-[#FF2442] hover:text-white uppercase text-xs tracking-widest font-bold flex items-center gap-2"><Trash2 size={14}/>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
